import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchPledgeReleaseCases, deletePledgeReleaseCase, restorePledgeReleaseCase } from '../api';
import {
  Table, Button, Space, Typography, Spin, Pagination, Badge, Switch, Tooltip,
  Row, Col, Grid, Card, Tag, message,
  theme as antdTheme, Dropdown,
} from 'antd';
import { PlusOutlined, EyeOutlined, EditOutlined, DeleteOutlined, FilterOutlined, MoreOutlined, UndoOutlined } from '@ant-design/icons';
import { useDeleteConfirm } from '../../../components/DeleteConfirm';
import { usePermissions } from '../../../hooks/usePermissions';
import { useAuth } from '../../../context/AuthContext';
import { fetchBranchById, fetchBranches } from '../../branches/api';
import { fetchAgents } from '../../agents/api';
import {
  CaseSearch, CaseFilterModal, ActiveFilterTags, COMMON_SEARCH_FIELDS, EMPTY_FILTERS, countActiveFilters, filtersToParams,
} from '../../../components/CaseListFilters';
import 'bootstrap/dist/css/bootstrap.min.css';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const STATUS_OPTIONS = [
  'LEAD_IDENTIFIED',
  'DOCS_VERIFIED',
  'ADVANCE_APPROVED',
  'PART_PAYMENT_DONE',
  'FULLY_PAID_AWAITING_RELEASE',
  'GOLD_RECEIVED',
  'VALUATION_COMPLETED',
  'PAYMENT_SETTLED',
  'BILL_GENERATED',
  'CLOSED',
];

const STATUS_COLORS = {
  LEAD_IDENTIFIED: 'blue',
  DOCS_VERIFIED: 'cyan',
  ADVANCE_APPROVED: 'geekblue',
  PART_PAYMENT_DONE: 'orange',
  FULLY_PAID_AWAITING_RELEASE: 'gold',
  GOLD_RECEIVED: 'lime',
  VALUATION_COMPLETED: 'purple',
  PAYMENT_SETTLED: 'volcano',
  BILL_GENERATED: 'magenta',
  CLOSED: 'green',
};

// Pledge cases also search lender and loan account; 'all' covers them too.
const SEARCH_FIELDS = [
  { ...COMMON_SEARCH_FIELDS[0], placeholder: 'Search case ID, name, phone, Aadhaar, lender…' },
  ...COMMON_SEARCH_FIELDS.slice(1),
  { value: 'lender', label: 'Lender', placeholder: 'Search by lender name…' },
  { value: 'loanAccount', label: 'Loan A/c No.', placeholder: 'Search by loan account number…' },
];

const statusLabel = (s) => s.replace(/_/g, ' ');

const formatCurrency = (val) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(val || 0);

// Once a Gold Receipt exists the case rate is locked and the receipt holds the
// measured figures — the case-level totals stay the pre-valuation estimate.
// Weights show the receipt's numbers wherever it exists, the estimate otherwise;
// the value columns keep the two figures side by side.
const getDisplayFigures = (c) => {
  const gr = c.goldReceipt;
  // finalNetValue also lands on the case itself at settlement finalisation, so
  // fall back to that for cases valued before the receipt join existed.
  const finalNetValue = gr?.finalNetValue ?? c.finalNetValue ?? 0;
  // The estimated counterpart of finalNetValue: both are net of the processing
  // fee and rounding, so the two columns compare like for like. Same formula as
  // the "Final Amount (to be used)" row on the case details page — computed
  // rather than read from the stored approxFinalAmount, which pre-dates some
  // cases and can lag an edit to the gold items.
  const estValue = parseFloat(
    ((c.estimatedTotalAmount || 0) - (c.processingFeeAmount || 0) + (c.roundingUpAmount || 0)).toFixed(2)
  );
  if (gr) {
    return {
      isFinal: true,
      grossWeight: gr.finalTotalGrossWeight,
      netWeight: gr.finalTotalNetWeight,
      estValue,
      finalNetValue,
    };
  }
  return {
    isFinal: false,
    grossWeight: c.totalGrossWeight,
    netWeight: c.totalNetWeight,
    estValue,
    finalNetValue,
  };
};

// Marks a cell as still being the pre-valuation estimate.
const EstTag = () => (
  <Tag color="default" style={{ fontSize: 10, marginLeft: 6, lineHeight: '16px' }}>EST</Tag>
);

const PledgeReleaseCaseList = () => {
  const { canViewModule, canViewCreateModule, canEditModule, can, canViewField, role } = usePermissions();
  const { user } = useAuth();

  const canViewCustomer = canViewField('pledgeReleaseCase', 'customerId');
  const canViewLender = canViewField('pledgeReleaseCase', 'lenderName');
  const canViewClosure = canViewField('pledgeReleaseCase', 'closureAmount');
  const canViewStatusField = canViewField('pledgeReleaseCase', 'status');
  const canViewGoldItems = canViewField('pledgeReleaseCase', 'goldItems');
  const canViewCapitalExposure = canViewField('pledgeReleaseCase', 'capitalExposure');

  const [cases, setCases] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchField, setSearchField] = useState('all');
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [isHO, setIsHO] = useState(false);
  const [branches, setBranches] = useState([]);
  const [agents, setAgents] = useState([]);
  const [showDeleted, setShowDeleted] = useState(false); // admin: list soft-deleted cases

  // Column order management
  const defaultColumnOrder = ['caseId', 'customer', 'branch', 'grossWt', 'netWt', 'closureAmount', 'estValue', 'finalNetValue', 'createdAt', 'status', 'actions'];
  const [columnOrder, setColumnOrder] = useState(() => {
    let saved;
    try {
      saved = JSON.parse(localStorage.getItem('pledgeReleaseCaseColumnOrder'));
    } catch (_) { saved = null; }
    if (!Array.isArray(saved) || !saved.length) return defaultColumnOrder;
    // Reconcile a previously saved order with the current column set: drop keys
    // that no longer exist and slot new ones in at their default position, so a
    // user who reordered columns before still sees columns added since.
    const known = new Set(defaultColumnOrder);
    const merged = saved.filter((k) => known.has(k));
    defaultColumnOrder.forEach((key, idx) => {
      if (!merged.includes(key)) merged.splice(Math.min(idx, merged.length), 0, key);
    });
    return merged;
  });

  const navigate = useNavigate();
  const { token } = antdTheme.useToken();
  const screens = useBreakpoint();
  const [messageApi, contextHolder] = message.useMessage();
  const isAdmin = role === 'admin';
  const { confirmDelete, confirmPermanentDelete, confirmRestore, contextHolder: deleteModalHolder } = useDeleteConfirm();

  // Detect HO user and load branch list for filter
  useEffect(() => {
    const init = async () => {
      try {
        if (isAdmin) {
          setIsHO(true);
        } else if (user?.branch) {
          const branchRes = await fetchBranchById(user.branch);
          const branch = branchRes?.data || branchRes;
          if (branch?.branchType === 'HO') setIsHO(true);
        }
        const res = await fetchBranches({ limit: 200 });
        const list = res.data || res;
        setBranches(Array.isArray(list) ? list.filter(b => b.branchType !== 'HO') : []);
      } catch (_) {}
      try {
        const res = await fetchAgents({ limit: 500 });
        const list = res.data || res;
        setAgents(Array.isArray(list) ? list : []);
      } catch (_) {}
    };
    init();
  }, [isAdmin, user?.branch]);

  // Handle column drag and drop
  const handleColumnDragStart = (e, columnKey) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('columnKey', columnKey);
  };

  const handleColumnDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleColumnDrop = (e, targetColumnKey) => {
    e.preventDefault();
    const draggedColumnKey = e.dataTransfer.getData('columnKey');
    
    if (draggedColumnKey === targetColumnKey) return;
    
    const draggedIdx = columnOrder.indexOf(draggedColumnKey);
    const targetIdx = columnOrder.indexOf(targetColumnKey);
    
    const newOrder = [...columnOrder];
    newOrder.splice(draggedIdx, 1);
    newOrder.splice(targetIdx, 0, draggedColumnKey);
    
    setColumnOrder(newOrder);
    localStorage.setItem('pledgeReleaseCaseColumnOrder', JSON.stringify(newOrder));
  };

  useEffect(() => {
    loadCases();
  }, [pagination.current, pagination.pageSize, searchQuery, searchField, filters, showDeleted]);

  const applyFilters = (next) => {
    setFilters(next);
    setPagination((prev) => ({ ...prev, current: 1 }));
    setFilterModalOpen(false);
  };

  const loadCases = async () => {
    setLoading(true);
    try {
      const response = await fetchPledgeReleaseCases({
        search: searchQuery,
        searchField: searchQuery ? searchField : '',
        page: pagination.current,
        limit: pagination.pageSize,
        includeGoldReceipt: true,
        ...filtersToParams(filters),
        deleted: showDeleted || '',
      });
      setCases(response.data || []);
      setPagination((prev) => ({ ...prev, total: response.meta?.total || 0 }));
    } catch (error) {
      messageApi.error('Failed to load pledge release cases');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setPagination((prev) => ({ ...prev, current: 1 }));
    setSearchQuery(value);
  };

  const handleDelete = async (id, permanent) => {
    try {
      await deletePledgeReleaseCase(id, { permanent });
      messageApi.success(permanent ? 'Case permanently deleted' : 'Case deleted successfully');
      loadCases();
    } catch (error) {
      console.error('Delete error:', error);
      messageApi.error(error?.response?.data?.message || 'Failed to delete case');
    }
  };

  const handleRestore = async (id) => {
    try {
      await restorePledgeReleaseCase(id);
      messageApi.success('Case restored');
      loadCases();
    } catch (error) {
      messageApi.error(error?.response?.data?.message || 'Failed to restore case');
    }
  };

  const columns = [
    {
      key: 'branch',
      title: 'Branch',
      render: (_, record) => record.branch?.name || '—',
      onHeaderCell: (column) => ({
        draggable: true,
        onDragStart: (e) => handleColumnDragStart(e, column.key),
        onDragOver: handleColumnDragOver,
        onDrop: (e) => handleColumnDrop(e, column.key),
        style: { cursor: 'move', userSelect: 'none' },
      }),
    },
    {
      key: 'caseId',
      title: 'Case ID',
      dataIndex: 'caseId',
      render: (text, record) => (
        <a onClick={() => navigate(`/pledge-release-cases/${record._id}`)}>{text}</a>
      ),
      onHeaderCell: (column) => ({
        draggable: true,
        onDragStart: (e) => handleColumnDragStart(e, column.key),
        onDragOver: handleColumnDragOver,
        onDrop: (e) => handleColumnDrop(e, column.key),
        style: { cursor: 'move', userSelect: 'none' },
      }),
    },
    {
      key: 'customer',
      title: 'Customer',
      render: (_, record) => record.customerId?.name ? (
        <>
          <div>{record.customerId.name}</div>
          {record.customerId.phone && <Text type="secondary" style={{ fontSize: 12 }}>{record.customerId.phone}</Text>}
        </>
      ) : '—',
      onHeaderCell: (column) => ({
        draggable: true,
        onDragStart: (e) => handleColumnDragStart(e, column.key),
        onDragOver: handleColumnDragOver,
        onDrop: (e) => handleColumnDrop(e, column.key),
        style: { cursor: 'move', userSelect: 'none' },
      }),
    },
    {
      key: 'grossWt',
      title: 'Gross Wt. (gm)',
      render: (_, record) => {
        const { grossWeight, isFinal } = getDisplayFigures(record);
        if (!grossWeight) return '—';
        return <>{grossWeight.toFixed(4)}{!isFinal && <EstTag />}</>;
      },
      onHeaderCell: (column) => ({
        draggable: true,
        onDragStart: (e) => handleColumnDragStart(e, column.key),
        onDragOver: handleColumnDragOver,
        onDrop: (e) => handleColumnDrop(e, column.key),
        style: { cursor: 'move', userSelect: 'none' },
      }),
    },
    {
      key: 'netWt',
      title: 'Net Wt. (gm)',
      render: (_, record) => {
        const { netWeight, isFinal } = getDisplayFigures(record);
        if (!netWeight) return '—';
        return <>{netWeight.toFixed(4)}{!isFinal && <EstTag />}</>;
      },
      onHeaderCell: (column) => ({
        draggable: true,
        onDragStart: (e) => handleColumnDragStart(e, column.key),
        onDragOver: handleColumnDragOver,
        onDrop: (e) => handleColumnDrop(e, column.key),
        style: { cursor: 'move', userSelect: 'none' },
      }),
    },
    {
      key: 'closureAmount',
      title: 'Loan Closure Amt.',
      dataIndex: 'closureAmount',
      render: (val) => formatCurrency(val),
      onHeaderCell: (column) => ({
        draggable: true,
        onDragStart: (e) => handleColumnDragStart(e, column.key),
        onDragOver: handleColumnDragOver,
        onDrop: (e) => handleColumnDrop(e, column.key),
        style: { cursor: 'move', userSelect: 'none' },
      }),
    },
    {
      key: 'estValue',
      title: 'Est. Final Amt.',
      render: (_, record) => {
        const { estValue } = getDisplayFigures(record);
        return estValue ? <Text type="secondary">{formatCurrency(estValue)}</Text> : '—';
      },
      onHeaderCell: (column) => ({
        draggable: true,
        onDragStart: (e) => handleColumnDragStart(e, column.key),
        onDragOver: handleColumnDragOver,
        onDrop: (e) => handleColumnDrop(e, column.key),
        style: { cursor: 'move', userSelect: 'none' },
      }),
    },
    {
      key: 'finalNetValue',
      title: 'Final Net Value',
      render: (_, record) => {
        const { finalNetValue } = getDisplayFigures(record);
        if (!finalNetValue) return '—';
        return <Text strong style={{ color: '#3f8600' }}>{formatCurrency(finalNetValue)}</Text>;
      },
      onHeaderCell: (column) => ({
        draggable: true,
        onDragStart: (e) => handleColumnDragStart(e, column.key),
        onDragOver: handleColumnDragOver,
        onDrop: (e) => handleColumnDrop(e, column.key),
        style: { cursor: 'move', userSelect: 'none' },
      }),
    },
    {
      key: 'createdAt',
      title: 'Created Date',
      dataIndex: 'createdAt',
      render: (val) => val ? new Date(val).toLocaleDateString('en-IN') : '—',
      onHeaderCell: (column) => ({
        draggable: true,
        onDragStart: (e) => handleColumnDragStart(e, column.key),
        onDragOver: handleColumnDragOver,
        onDrop: (e) => handleColumnDrop(e, column.key),
        style: { cursor: 'move', userSelect: 'none' },
      }),
    },
    {
      key: 'status',
      title: 'Status',
      dataIndex: 'status',
      render: (status, record) => (
        <>
          <Tag color={STATUS_COLORS[status] || 'default'} style={{ fontSize: 11 }}>
            {(status || '').replace(/_/g, ' ')}
          </Tag>
          {record.isDeleted && (
            <Tooltip title={`Deleted${record.deletedBy?.name ? ` by ${record.deletedBy.name}` : ''}${record.deletedAt ? ` on ${new Date(record.deletedAt).toLocaleString('en-IN')}` : ''}`}>
              <Tag color="red" style={{ fontSize: 11 }}>DELETED</Tag>
            </Tooltip>
          )}
        </>
      ),
      onHeaderCell: (column) => ({
        draggable: true,
        onDragStart: (e) => handleColumnDragStart(e, column.key),
        onDragOver: handleColumnDragOver,
        onDrop: (e) => handleColumnDrop(e, column.key),
        style: { cursor: 'move', userSelect: 'none' },
      }),
    },
    {
      key: 'actions',
      title: 'Action',
      width: 60,
      align: 'center',
      render: (_, record) => {
        const what = `case ${record.caseId}`;
        const actionItems = record.isDeleted ? [
          { key: 'view', label: 'View', icon: <EyeOutlined />, onClick: () => navigate(`/pledge-release-cases/${record._id}`) },
          { key: 'restore', label: 'Restore', icon: <UndoOutlined />, onClick: () => confirmRestore({ what, onRestore: () => handleRestore(record._id) }) },
          {
            key: 'purge',
            label: 'Delete permanently',
            icon: <DeleteOutlined />,
            danger: true,
            onClick: () => confirmPermanentDelete({ what, onDelete: () => handleDelete(record._id, true) }),
          },
        ] : [
          {
            key: 'view',
            label: 'View',
            icon: <EyeOutlined />,
            onClick: () => navigate(`/pledge-release-cases/${record._id}`),
          },
          canEditModule('pledgeReleaseCase') && {
            key: 'edit',
            label: 'Edit',
            icon: <EditOutlined />,
            onClick: () => navigate(`/pledge-release-cases/edit/${record._id}`),
          },
          can('pledgeReleaseCase', 'delete') && {
            key: 'delete',
            label: 'Delete',
            icon: <DeleteOutlined />,
            onClick: () => confirmDelete({ what, onDelete: (permanent) => handleDelete(record._id, permanent) }),
            danger: true,
          },
        ].filter(Boolean);

        return (
          <Dropdown menu={{ items: actionItems }} trigger={['click']}>
            <Button type="text" icon={<MoreOutlined />} size="small" />
          </Dropdown>
        );
      },
      onHeaderCell: (column) => ({
        style: { cursor: 'default' },
      }),
    },
  ];

  // Sort columns by columnOrder
  const sortedColumns = columnOrder
    .map(key => columns.find(col => col.key === key))
    .filter(Boolean);

  return (
    <>
      {contextHolder}
      {deleteModalHolder}
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <Card>
              <Row justify="space-between" align="middle" className="mb-4">
                <Col>
                  <Title level={2} style={{ margin: 0 }}>Pledge Release Cases{showDeleted && <Text type="danger"> — Deleted</Text>}</Title>
                </Col>
                <Col>
                  <Space>
                    <CaseSearch
                      fields={SEARCH_FIELDS}
                      field={searchField}
                      onFieldChange={(f) => { setSearchField(f); setPagination((prev) => ({ ...prev, current: 1 })); }}
                      onSearch={handleSearch}
                      size={screens.xs ? 'small' : 'middle'}
                    />
                    {isAdmin && (
                      <Space size={4}>
                        <Switch
                          size="small"
                          checked={showDeleted}
                          onChange={(v) => { setShowDeleted(v); setPagination((prev) => ({ ...prev, current: 1 })); }}
                        />
                        <Text type={showDeleted ? 'danger' : 'secondary'}>Show deleted</Text>
                      </Space>
                    )}
                    <Badge count={countActiveFilters(filters)} size="small">
                      <Button
                        type="primary"
                        icon={<FilterOutlined />}
                        onClick={() => setFilterModalOpen(true)}
                        size={screens.xs ? 'small' : 'middle'}
                      >
                        {screens.xs ? '' : 'Filters'}
                      </Button>
                    </Badge>
                    {canViewCreateModule('pledgeReleaseCase') && (
                      <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => navigate('/pledge-release-cases/create')}
                        size={screens.xs ? 'small' : 'middle'}
                      >
                        {screens.xs ? '' : 'New Case'}
                      </Button>
                    )}
                  </Space>
                </Col>
              </Row>

              <CaseFilterModal
                open={filterModalOpen}
                onClose={() => setFilterModalOpen(false)}
                filters={filters}
                onApply={applyFilters}
                isHO={isHO}
                branches={branches}
                agents={agents}
                statusOptions={STATUS_OPTIONS}
                statusLabel={statusLabel}
                amountLabel="Loan Closure Amount (₹)"
              />

              <ActiveFilterTags
                filters={filters}
                onChange={applyFilters}
                branches={branches}
                agents={agents}
                statusLabel={statusLabel}
                fmtAmount={formatCurrency}
              />

              <Spin spinning={loading}>
                {screens.md ? (
                  <Table
                    columns={sortedColumns}
                    dataSource={cases}
                    pagination={false}
                    rowKey={(record) => record._id}
                    scroll={{ x: 'max-content' }}
                    size={screens.lg ? 'middle' : 'small'}
                  />
                ) : (
                  <div>
                    {cases.length ? (
                      cases.map((c) => {
                        const { estValue, finalNetValue, netWeight } = getDisplayFigures(c);
                        return (
                        <Card
                          key={c._id}
                          size="small"
                          hoverable
                          style={{ marginBottom: 12 }}
                          onClick={() => navigate(`/pledge-release-cases/${c._id}`)}
                        >
                          <Row justify="space-between" align="middle">
                            <Col span={16}>
                              <Title level={5} style={{ marginBottom: 0 }}>{c.caseId}</Title>
                              <Text type="secondary" style={{ display: 'block' }}>{c.lenderName}</Text>
                              <Text type="secondary" style={{ display: 'block' }}>{c.customerId?.name || '-'}</Text>
                            </Col>
                            <Col span={8} style={{ textAlign: 'right' }}>
                              <Tag color={STATUS_COLORS[c.status] || 'default'} style={{ fontSize: 10 }}>
                                {(c.status || '').replace(/_/g, ' ')}
                              </Tag>
                              <div style={{ marginTop: 4 }}>
                                <Text strong>{formatCurrency(c.closureAmount)}</Text>
                              </div>
                              {estValue > 0 && (
                                <div style={{ marginTop: 2 }}>
                                  <Text type="secondary" style={{ fontSize: 11 }}>Est: {formatCurrency(estValue)}</Text>
                                </div>
                              )}
                              {finalNetValue > 0 && (
                                <div style={{ marginTop: 2 }}>
                                  <Text strong style={{ fontSize: 11, color: '#3f8600' }}>Final: {formatCurrency(finalNetValue)}</Text>
                                </div>
                              )}
                              {netWeight > 0 && (
                                <div style={{ marginTop: 2 }}>
                                  <Text type="secondary" style={{ fontSize: 11 }}>Net Wt: {netWeight.toFixed(4)} gm</Text>
                                </div>
                              )}
                            </Col>
                          </Row>
                        </Card>
                        );
                      })
                    ) : (
                      <div style={{ textAlign: 'center', padding: 24 }}>
                        <Text type="secondary">No cases found.</Text>
                      </div>
                    )}
                  </div>
                )}

                {pagination.total > 0 && (
                  <Row justify="center" className="mt-4">
                    <Col>
                      <Pagination
                        current={pagination.current}
                        pageSize={pagination.pageSize}
                        total={pagination.total}
                        onChange={(page, pageSize) => setPagination((prev) => ({ ...prev, current: page, pageSize }))}
                        showSizeChanger
                        showQuickJumper={!screens.xs}
                        showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} cases`}
                        size={screens.xs ? 'small' : 'default'}
                        responsive
                      />
                    </Col>
                  </Row>
                )}
              </Spin>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
};

export default PledgeReleaseCaseList;
